import React, { useState, useMemo, useEffect } from 'react';
import {
  Button,
  Space,
  Table,
  Tree,
  Input,
  Popconfirm,
} from '@bedrock/components';
import { uniqueId, isEqual } from 'lodash';
import { Plus, Edit, Delete } from '@bedrock/icons-react';
import classNames from 'classnames/bind';
import { useMemoizedFn, useControllableValue } from 'ahooks';
import styles from './index.less';

const cx = classNames.bind(styles);

export type ShowTree = {
  value: string;
  key?: string;
  id: string;
  level?: number;
  parentId?: string;
  removed?: boolean;
  children?: ShowTree[];
};

// Tree数据结构
// 主要解决tree操作的问题
class TreeData {
  dataMap: Map<string, ShowTree> = new Map();
  maxLevel: number = 4;

  constructor(data: ShowTree[], maxLevel: number = 4) {
    this.dataMap = new Map();
    this.maxLevel = maxLevel;
    this.expandChildren(data, 'root');
  }

  // 扩展节点
  expandChildren(tree: ShowTree[], parentId: string) {
    if (!this.dataMap.has(parentId)) {
      this.dataMap.set(parentId, {
        value: '',
        id: parentId,
        level: 0,
        children: tree,
      });
    }
    tree?.forEach((item, index) => {
      if (!item.id) {
        item.id = uniqueId();
      }
      if (parentId !== 'root') {
        let parentLevel = this.dataMap.get(parentId).level || 0;
        item.level = parentLevel + 1;
      } else {
        item.level = 1;
      }
      item.parentId = parentId;
      this.dataMap.set(item.id, item);
      this.expandChildren(item?.children, item.id);
    });
  }

  get data() {
    return this.showTree('root');
  }

  // 获取table数据
  getTableList(id, needAddLevels = []) {
    const treeToTable = (
      tree: ShowTree[],
      parent: any,
      tableData: any[] = []
    ) => {
      tree?.forEach((item) => {
        let parentData = {
          ...parent,
          ['level-' + item.level]: item,
          key: item.id,
        };
        if (item?.children?.length) {
          treeToTable(item.children, parentData, tableData);
        } else {
          parentData['$last'] = item;
          tableData.push(parentData);
        }
      });
      const ableAdd = needAddLevels?.includes(tree?.[0]?.level);
      if (ableAdd && tree?.length > 0) {
        tableData.push({
          ...parent,
          addLevel: tree[0].level,
          parentId: parent.key,
        });
      }
      return tableData;
    };
    let tableList = treeToTable(this.showTree(id), {
      key: id,
    });
    return tableList;
  }

  showTree(id: string): ShowTree[] {
    const node = this.dataMap.get(id);
    if (node?.removed) {
      return [];
    }
    return node?.children
      ?.filter((n) => !n.removed)
      .map((item) => {
        return {
          ...item,
          children: this.showTree(item.id),
        };
      });
  }

  // 添加节点
  addDefaultChildren(id: string) {
    const node = this.dataMap.get(id);
    if (node.level >= this.maxLevel) {
      return;
    }
    let childrenLevel = this.maxLevel - node.level;
    let childrenNodeTarget = {
      value: '',
      id: uniqueId(),
      children: [],
    };
    let childrenNodeTemp = childrenNodeTarget;
    while (childrenLevel > 0) {
      childrenNodeTemp.children = [
        {
          value: '',
          id: uniqueId(),
          children: [],
        },
      ];
      childrenNodeTemp = childrenNodeTemp.children[0];
      childrenLevel--;
    }
    node.children.push(childrenNodeTarget);
    this.expandChildren([childrenNodeTarget], id);
  }

  // 删除节点
  removeNode(id: string) {
    const node = this.dataMap.get(id);
    node.removed = true;
    this.dataMap.set(id, node);
  }

  // 更新节点值
  updateNodeValue(id: string, value: string) {
    const node = this.dataMap.get(id);
    node.value = value;
    this.dataMap.set(id, node);
  }

  updateChildrenNodeValue(id: string, value: string) {
    const node = this.dataMap.get(id);
    node?.children?.forEach((item) => {
      item.value = value;
      this.updateNodeValue(item.id, value);
    });
  }
}

// 默认渲染单元格, 编辑单元格
export function defaultRenderCellValue(
  cellState: {
    value: any;
    setValue: (value: any) => void;
    updateNodeValue?: (itemId: string, value: any) => void;
    updateChildrenNodeValue?: (itemId: string, value: any) => void;
  },
  cellParams: {
    record: any;
    dataIndex: string;
    colItem: {
      value: any;
    } & any;
    index: number;
  }
): React.ReactNode | React.JSX.Element {
  return (
    <Input.TextArea
      size="small"
      className={cx('textarea', {
        'has-value': !!cellState.value,
      })}
      rows={1}
      autoSize
      style={{ width: '100%' }}
      border={false}
      value={cellState.value}
      onChange={(e) => cellState.setValue(e.target.value)}
    />
  );
}

export type RenderCellValue = typeof defaultRenderCellValue;

function treeTemplate(level: number) {
  let treeData = [];
  let children = treeData;
  while (level > 0) {
    children.push({
      value: '',
      children: [],
    });
    children = children[0].children;
    level--;
  }
  return treeData;
}

// 推导类型
type TreeToTableProps = {
  renderCellValue?: RenderCellValue;
  requiredLevels?: number[];
  value: ShowTree[];
  onChange: (value: ShowTree[]) => void;
  treeLevel?: number;
  needAddLevels?: number[];
  tableTitleList?: {
    text: string;
    render?: () => React.ReactNode | React.JSX.Element;
  }[];
  tableProps?: Omit<React.ComponentProps<typeof Table>, 'data'>;
  tableDataFilter?: (item: any) => boolean;
};

export default function TreeToTable({
  renderCellValue = defaultRenderCellValue,
  requiredLevels,
  tableTitleList = [],
  treeLevel = 4,
  needAddLevels = [],
  tableProps = {
    columns: [],
  },
  tableDataFilter = (item) => true,
  ...otherProps
}: TreeToTableProps) {
  const [treeData, setTreeData] = useControllableValue(otherProps, {
    defaultValue: treeTemplate(treeLevel),
  });
  const { columns = [], ...restTableProps } = tableProps;

  const treeDataRef = React.useRef(new TreeData(treeData));
  const showTree = useMemo(() => {
    if (isEqual(treeData, treeDataRef.current.data)) {
      return treeDataRef.current.data;
    }
    treeDataRef.current = new TreeData(treeData, treeLevel);
    return treeDataRef.current.data;
  }, [treeData]);

  const requiredLevelsLocal = useMemo(() => {
    if (requiredLevels) {
      return requiredLevels;
    }
    let levels = [];
    for (let i = 1; i <= treeLevel; i++) {
      levels.push(i);
    }
    return levels;
  }, [requiredLevels, treeLevel]);

  const addLevel = (id: string) => {
    treeDataRef.current?.addDefaultChildren(id);
    setTreeData(treeDataRef.current.data);
  };

  const removeNode = (id: string) => {
    treeDataRef.current?.removeNode(id);
    setTreeData(treeDataRef.current.data);
  };

  const updateNodeValue = useMemoizedFn((id: string, value: string) => {
    treeDataRef.current?.updateNodeValue(id, value);
    setTreeData(treeDataRef.current.data);
  });

  const updateChildrenNodeValue = useMemoizedFn((id: string, value: string) => {
    treeDataRef.current?.updateChildrenNodeValue(id, value);
    setTreeData(treeDataRef.current.data);
  });

  const tableData = useMemo(() => {
    // 将tree改成数表格数据
    return (
      treeDataRef.current
        ?.getTableList?.('root', needAddLevels)
        .filter(tableDataFilter) || []
    );
  }, [treeData, tableDataFilter]);

  const renderTitle = (level: number, type: 'text' | 'render') => {
    let title = tableTitleList?.[level - 1];
    if (type === 'text') {
      return title?.text;
    }
    return (
      <div
        className={cx('table-title', {
          required:
            requiredLevelsLocal.includes(level) &&
            needAddLevels?.includes?.(level),
        })}
      >
        {title?.render?.() || title?.text}
      </div>
    );
  };

  const columnsRender = useMemoizedFn(
    (dataIndex: string, item, record, index) => {
      // 添加按钮
      if (typeof record?.addLevel !== 'undefined') {
        if (!needAddLevels?.includes(record?.addLevel)) {
          return {
            children: '',
            props: {
              colSpan: 0,
              rowSpan: 0,
            },
          };
        }
        if ('level-' + record.addLevel === dataIndex) {
          const parentId = record.parentId;
          return {
            children: (
              <Button
                className={cx('add-level')}
                size="small"
                type={'link'}
                onClick={() => addLevel(parentId)}
              >
                <Plus /> 添加{renderTitle(record.addLevel, 'text')}
              </Button>
            ),
            props: {
              style: {
                textAlign: 'left',
              },
              rowSpan: 1,
              colSpan: treeLevel - (record.addLevel - 1),
            },
          };
        }
        return {
          children: '',
          props: {
            colSpan: 0,
            rowSpan: 0,
          },
        };
      }

      // 普通编辑表格
      const parentId = record[dataIndex]?.parentId;
      const parentTree = treeDataRef.current?.showTree(parentId);
      let rowSpan = 1;
      if (record[dataIndex]?.id === tableData[index - 1]?.[dataIndex]?.id) {
        rowSpan = 0;
      } else {
        // 向下合并id相同 rowspan
        let i = 1;
        while (
          record[dataIndex]?.id &&
          record[dataIndex]?.id === tableData[index + i]?.[dataIndex]?.id
        ) {
          i++;
          rowSpan++;
        }
      }

      return {
        children: (
          <>
            {
              <div className={cx('input-wrapper')}>
                {renderCellValue?.(
                  {
                    value: item?.value,
                    setValue: (value) => updateNodeValue(item.id, value),
                    updateNodeValue,
                    updateChildrenNodeValue,
                  },
                  {
                    record,
                    dataIndex,
                    colItem: item,
                    index,
                  }
                )}
                {!!needAddLevels?.includes(item?.level) && (
                  <Popconfirm
                    onOk={() => {
                      removeNode(record[dataIndex]?.id);
                    }}
                    type="warning"
                    style={{ width: '280px' }}
                    content="确定删除该单元格吗？该单元格及其关联子单元格将一并删除"
                    trigger="click"
                    placement="top"
                    disabled={parentTree?.length <= 1}
                  >
                    <Button
                      size="small"
                      type="text-subtle"
                      danger
                      icon={<Delete />}
                      disabled={parentTree?.length <= 1}
                    />
                  </Popconfirm>
                )}
              </div>
            }
          </>
        ),
        props: {
          className: cx('table-cell'),
          rowSpan: rowSpan,
        },
      };
    }
  );

  const allColumns = useMemo(() => {
    let treeColumns = [];
    for (let i = 1; i <= treeLevel; i++) {
      const treeColumnConfig =
        columns.find((item) => {
          return item?.dataIndex === 'level-' + i;
        }) || {};
      treeColumns.push({
        align: 'left',
        ...treeColumnConfig,
        title: renderTitle(i, 'render'),
        dataIndex: 'level-' + i,
        render: (item, record, index) => {
          return columnsRender('level-' + i, item, record, index);
        },
      });
    }
    const moreColumns = columns.filter((item, index) => {
      return !(item?.dataIndex + '').startsWith?.('level-');
    });
    treeColumns.push(...moreColumns);

    return treeColumns;
  }, [renderTitle, columnsRender, treeLevel, columns]);
  return (
    <div className={cx('tree-to-table')}>
      <Table
        rowKey={(record, index) => index}
        border
        verticalBorder
        size="xSmall"
        {...restTableProps}
        data={tableData}
        columns={allColumns}
      ></Table>
    </div>
  );
}
